import * as Yup from 'yup';

import { isIreland } from 'src/lib/countryConfig';

import { PricingTerm, SalesConditionsType, pricingTypes } from '../../hooks/usePricings/index';

// #region ProductPricingValidation
export const customTermValidation = Yup.number().when('term', {
    is: (term: string) => {
        return term === PricingTerm.CUSTOM;
    },
    then: Yup.number()
        .required('Custom Term is required')
        .typeError('Custom Term is required')
        .min(1, 'Custom Term must be at least 1')
        .when([], {
            is: () => {
                return !isIreland;
            },
            then: (schema) => {
                return schema
                    .min(1, 'Custom Term must be at least 1')
                    .max(18, 'Custom Term cannot exceed 18');
            },
            otherwise: (schema) => {
                return schema;
            },
        }),
    otherwise: Yup.number().optional().nullable(),
});

export const productPricingValidation = (selectedPrimaryProductIds: any[], fieldIds: any[]) => {
    const schemaFields = {
        name: Yup.string().required('Name is required'),
        term: Yup.string()
            .required('Contract term is required')
            .typeError('Contract term is required'),
        type: Yup.string().optional().nullable(),
        customTerm: customTermValidation,
        selectedPrimaryProductIds: Yup.array().of(Yup.number()).min(1, 'Required'),
        salesConditions: Yup.string()
            .oneOf([SalesConditionsType.YES, SalesConditionsType.NO])
            .required('Required'),
        gpvConditions: Yup.string()
            .oneOf([SalesConditionsType.YES, SalesConditionsType.NO])
            .required('Required'),
        gpvLowest: Yup.number().when('gpvConditions', {
            is: SalesConditionsType.YES,
            then: Yup.number().typeError('Must be a number').required('Required').nullable(),
            otherwise: Yup.number().nullable(),
        }),
        gpvHighest: Yup.number().when('gpvConditions', {
            is: SalesConditionsType.YES,
            then: Yup.number().typeError('Must be a number').required('Required').nullable(),
            otherwise: Yup.number().nullable(),
        }),
        mccConditions: Yup.string()
            .oneOf([SalesConditionsType.YES, SalesConditionsType.NO])
            .required('Required'),
        mccs: Yup.array().when('mccConditions', {
            is: (mccConditions: SalesConditionsType) => {
                return mccConditions === SalesConditionsType.YES;
            },
            then: Yup.array().required('Select MCC').min(1, 'Select at least one MCC').nullable(),
        }),
    };

    selectedPrimaryProductIds.forEach((productId: number) => {
        schemaFields[`oneOff-lowest-${productId}`] = Yup.number().when('type', {
            is: (value: string) => {
                return value === pricingTypes.ONE_OFF || value === pricingTypes.BOTH;
            },
            then: Yup.number()
                .typeError('Must be a number')
                .required('Required')
                .lessThan(Yup.ref(`oneOff-highest-${productId}`), 'Too high'),
        });
        schemaFields[`oneOff-highest-${productId}`] = Yup.number().when('type', {
            is: (value: string) => {
                return value === pricingTypes.ONE_OFF || value === pricingTypes.BOTH;
            },
            then: Yup.number()
                .typeError('Must be a number')
                .required('Required')
                .moreThan(Yup.ref(`oneOff-lowest-${productId}`), 'Too low'),
        });
        schemaFields[`monthly-lowest-${productId}`] = Yup.number().when('type', {
            is: (value: string) => {
                return value === pricingTypes.MONTHLY || value === pricingTypes.BOTH;
            },
            then: Yup.number()
                .typeError('Must be a number')
                .required('Required')
                .lessThan(Yup.ref(`monthly-highest-${productId}`), 'Too high'),
        });
        schemaFields[`monthly-highest-${productId}`] = Yup.number().when('type', {
            is: (value: string) => {
                return value === pricingTypes.MONTHLY || value === pricingTypes.BOTH;
            },
            then: Yup.number()
                .typeError('Must be a number')
                .required('Required')
                .moreThan(Yup.ref(`monthly-lowest-${productId}`), 'Too low'),
        });

        fieldIds.forEach((fieldId: number) => {
            schemaFields[`oneOffLimit-${productId}[${fieldId}].sales`] = Yup.number().when('type', {
                is: (value: string) => {
                    return value === pricingTypes.ONE_OFF || value === pricingTypes.BOTH;
                },
                then: Yup.number()
                    .required('Required')
                    .lessThan(Yup.ref(`oneOff-lowest-${productId}`), 'Too low')
                    .moreThan(Yup.ref(`oneOff-highest-${productId}`), 'Too high'),
            });

            schemaFields[`monthlyLimit-${productId}[${fieldId}].sales`] = Yup.number().when(
                'type',
                {
                    is: (value: string) => {
                        return value === pricingTypes.MONTHLY || value === pricingTypes.BOTH;
                    },
                    then: Yup.number()
                        .required('Required')
                        .lessThan(Yup.ref(`monthly-lowest-${productId}`), 'Too low')
                        .moreThan(Yup.ref(`monthly-highest-${productId}`), 'Too high'),
                }
            );
            schemaFields[`oneOffLimit-${productId}[${fieldId}].commission`] = Yup.number().when(
                'type',
                {
                    is: (value: string) => {
                        return value === pricingTypes.ONE_OFF || value === pricingTypes.BOTH;
                    },
                    then: Yup.number().required('Required'),
                }
            );
            schemaFields[`monthlyLimit-${productId}[${fieldId}].commission`] = Yup.number().when(
                'type',
                {
                    is: (value: string) => {
                        return value === pricingTypes.MONTHLY || value === pricingTypes.BOTH;
                    },
                    then: Yup.number().required('Required'),
                }
            );
        });
    });

    schemaFields.gpvLowest = schemaFields.gpvLowest.when('gpvConditions', {
        is: SalesConditionsType.YES,
        then: Yup.number().test({
            name: 'gpv-lowest-less-than-highest',
            message: 'Too High',
            test(value) {
                const { gpvHighest } = this.parent;
                return value < gpvHighest;
            },
        }),
    });
    schemaFields.gpvHighest = schemaFields.gpvHighest.when('gpvConditions', {
        is: SalesConditionsType.YES,
        then: Yup.number().test({
            name: 'gpv-highest-greater-than-lowest',
            message: 'Too Low',
            test(value) {
                const { gpvLowest } = this.parent;
                return value > gpvLowest;
            },
        }),
    });
    return Yup.object().shape(schemaFields);
};
